import axios from 'axios';

let _isLoggingOut = false;
const navigateToLogin = () => {
  if (_isLoggingOut) return;       // prevent alert storm from multiple 401s
  _isLoggingOut = true;
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('lu');
  sessionStorage.removeItem('auth');
  localStorage.removeItem('auth');  // clear stale auth so PrivateRoute redirects cleanly
  localStorage.removeItem('myc_pwa_dismissed_at');
  window.location.href = '/login';
};

/* ── GPS position cache (in-memory only) ─────────────────────────────────────
 * Cached position is attached to every API request as x-userlat / x-userlon
 * so the backend geo-fence can validate location on every call (not just login).
 * Starts null — set by Login.js via updateGpsCache() after getGeoCoords().
 * Refreshes every 3 minutes in the background once permission is granted.
 * No sessionStorage — each tab/login starts fresh (avoids cross-user stale coords).
 */
// Seed from sessionStorage so GPS is available immediately on page refresh
let _gps = (() => {
  try {
    const s = sessionStorage.getItem('lastGps');
    return s ? JSON.parse(s) : null;
  } catch { return null; }
})();

async function _refreshGps() {
  if (!navigator.geolocation) return;
  try {
    if (navigator.permissions) {
      const perm = await navigator.permissions.query({ name: 'geolocation' });
      if (perm.state === 'denied') return;
      if (perm.state === 'prompt') return;   // don't trigger browser prompt — login handles that
    }
  } catch (_) { /* permissions API not supported — fall through */ }
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      _gps = { lat: pos.coords.latitude, lon: pos.coords.longitude };
      try { sessionStorage.setItem('lastGps', JSON.stringify(_gps)); } catch {}
    },
    () => {},
    { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
  );
}
_refreshGps();
setInterval(_refreshGps, 3 * 60 * 1000);

/** Reset the logout guard — call from Login.js on mount so fresh login works. */
export function resetLogoutGuard() { _isLoggingOut = false; }

/**
 * Immediately set the in-memory GPS cache.
 * Called from Login.js after getGeoCoords() so post-login API calls
 * use the same accurate coords that passed the login geo-fence.
 */
export function updateGpsCache(lat, lon) {
  _gps = { lat, lon };
  try { sessionStorage.setItem('lastGps', JSON.stringify(_gps)); } catch {}
}

/** Return current cached GPS coords (or null if not yet obtained). */
export function getGpsCache() {
  return _gps;
}

const api = axios.create({
  baseURL: process.env.REACT_APP_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

/**
 * Sends combined consultation data to the backend
 *
 * @param {Object} params
 * @param {Event} params.event - DOM event
 * @param {string} params.stype - Section type (e.g., 'Knee', 'Neck')
 * @param {string} params.patientCard - Patient ID or card reference
 * @param {Array} params.sequences - Sequence data
 * @param {Array} params.recommendations - Recommendation data
 * @param {Array} params.services - Services data
 * @returns {Promise<boolean>} true on success, false on failure
 */
export const sendCombinedChangeCommon = async (  event,  stype,  patientCard,  payload) => {
  if (event?.preventDefault) event.preventDefault();
  console.log("combinedChange =", patientCard, stype, payload);

  try {
    const response = await api.post(
      `${process.env.REACT_APP_URL}/patients/consultation/combined/${patientCard}`,
      payload
    );

    console.log("✅ Data sent successfully:", response.data);
    alert("Updated successfully!");
    return true;
  } catch (error) {
    console.error("❌ Error sending data:", error);
    alert("Error updating data. Please try again!");
    return false;
  }
};

// Request Interceptor — attaches JWT, username, and cached GPS coords
api.interceptors.request.use(
  (config) => {
    const token    = sessionStorage.getItem('token');
    const username = sessionStorage.getItem('lu');

    if (token)    config.headers['Authorization'] = `Bearer ${token}`;
    if (username) config.headers['x-username']    = username;

    // Attach last-known GPS so backend can geo-fence every request
    if (_gps) {
      config.headers['x-userlat'] = String(_gps.lat);
      config.headers['x-userlon'] = String(_gps.lon);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ── Auto-recover from IP mismatch (mobile networks rotate IPs) ────────────
// When backend returns 401 + ipBlocked, call session-restore to re-register
// the new IP, then transparently retry the original request. Only logout if
// the token itself is expired/invalid.
let _isRestoringSession = false;
let _restorePromise = null;

function _doSessionRestore() {
  if (_restorePromise) return _restorePromise;
  _isRestoringSession = true;
  const token = sessionStorage.getItem('token');
  const lu = sessionStorage.getItem('lu');
  const base = process.env.REACT_APP_URL || '';
  _restorePromise = fetch(base + '/users/session-restore', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + (token || ''),
      'x-username': lu || '',
    },
  })
    .then(function (r) { return r.ok; })
    .catch(function () { return false; })
    .then(function (ok) {
      _isRestoringSession = false;
      _restorePromise = null;
      return ok;
    });
  return _restorePromise;
}

// Response Interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response) {
      const { status, data, config } = error.response;
      const url = config?.url || '';

      if (status === 401) {
        // Skip interceptor for login/auth endpoints — let the Login component
        // handle these errors inline (wrong password, wrong OTP, expired OTP, etc.)
        const isAuthEndpoint = url.includes('/login') || url.includes('/verify-login-otp')
          || url.includes('/saadhak-otp/') || url.includes('/saadhaklogin')
          || url.includes('/kiosk-otp/');
        if (!isAuthEndpoint && !url.includes('/gdrive/') && !_isLoggingOut) {
          // IP changed (mobile network rotation) — try session-restore + retry once
          if (data?.ipBlocked && !config._ipRetried) {
            const restored = await _doSessionRestore();
            if (restored) {
              config._ipRetried = true;
              return api(config); // retry original request with same config
            }
          }
          // Token genuinely expired OR session-restore failed — log out
          _isLoggingOut = true;
          sessionStorage.removeItem('token');
          sessionStorage.removeItem('lu');
          localStorage.removeItem('auth');
          const msg = data?.ipBlocked
            ? (data.message || 'Session invalid: network changed. Please log in again.')
            : 'Session Expired / Unauthorized access';
          alert(msg);
          window.location.href = '/login';
        }
      }

      // GDrive disconnected — broadcast global event so any page can show banner
      if (status === 503 && data?.error === 'GDRIVE_REAUTH') {
        window.dispatchEvent(new CustomEvent('gdrive-disconnected'));
      }

      if (status === 403 && data?.geoBlocked) {
        // Skip alert for background geo-ping requests
        const reqUrl = config?.url || '';
        if (reqUrl.includes('/geo-ping')) return Promise.reject(error);
        // Debounce: show only one geo-blocked alert per 10 seconds
        const now = Date.now();
        if (!api._lastGeoAlert || now - api._lastGeoAlert > 10000) {
          api._lastGeoAlert = now;
          alert(data.message || 'Access denied: outside allowed location.');
        }
        // Do NOT navigate to login — just block the action
      }

      if (status === 400 && data?.message) {
        // Skip alert for login/auth endpoints — let their own catch blocks handle it
        const isAuthEndpoint = url.includes('/login') || url.includes('/verify-login-otp')
          || url.includes('/saadhak-otp/') || url.includes('/saadhaklogin')
          || url.includes('/kiosk-otp/');
        if (!isAuthEndpoint) alert(data.message);
      }
    }

    return Promise.reject(error);
  }
);

/* ── Geo-ping: 20-min interval while attendance is IN_PROGRESS ──────────────
 * Starts on login/check-in. Sends GPS to backend for geo-presence tracking.
 * Backend alerts owner/solution if user is outside fence for 4+ consecutive pings.
 */
let _geoPingInterval = null;

export function startGeoPingIfCheckedIn() {
  if (_geoPingInterval) { clearInterval(_geoPingInterval); _geoPingInterval = null; }

  const username = sessionStorage.getItem('lu');
  const token = sessionStorage.getItem('token');
  if (!username || !token) return;

  api.get(`${process.env.REACT_APP_URL}/myattendance/today/${username}`)
    .then(({ data }) => {
      if (data?.status !== 'IN_PROGRESS') return;

      const sendPing = () => {
        if (!navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            api.post(`${process.env.REACT_APP_URL}/myattendance/geo-ping`, {
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
            }).catch(() => {});
          },
          () => {},
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
        );
      };

      sendPing();
      _geoPingInterval = setInterval(sendPing, 20 * 60 * 1000);
    })
    .catch(() => {});
}

export function stopGeoPing() {
  if (_geoPingInterval) { clearInterval(_geoPingInterval); _geoPingInterval = null; }
}

export default api;
